/// <amd-module name="Test/ManageTestPlans/Components/TestPlanComponentBase" />
import { IContextualMenuItem } from "OfficeFabric/ContextualMenu";
import {
    ColumnActionsMode,
    ConstrainMode,
    DetailsListLayoutMode,
    DetailsRow,
    IColumn,
    IDetailsRowProps,
} from "OfficeFabric/DetailsList";
import { Link } from "VSSUI/Link";
import { SelectionMode } from "OfficeFabric/Selection";
import { getId as getTooltipId } from "OfficeFabric/Utilities";
import { css } from "VSSUI/Util";
import * as React from "react";
import { TestPlanDirectoryActionsCreator } from "Test/ManageTestPlans/Actions/TestPlanDirectoryActionsCreator";
import { LoadingComponent } from "Test/ManageTestPlans/Components/LoadingComponent";
import { isAdvancedTestExtensionEnabled } from "Test/ManageTestPlans/LicenseUtils";
import * as Resources from "Test/ManageTestPlans/Resources";
import { TestPlanDirectoryStore } from "Test/ManageTestPlans/Stores/TestPlanDirectoryStore";
import { TelemetryConstants,ManageTestPlansCommonConstants } from "Test/ManageTestPlansCommon/Constants";
import {
    IDirectoryRow,
    ITestPlanDirectoryListComponentState,
    ITestPlanRow,
} from "Test/ManageTestPlansCommon/Contracts";
import { FavoriteState, TestPlanPivotColumnKeys, WorkItemField } from "Test/ManageTestPlansCommon/Constants";
import { ITfsPageService } from "Tfs/Platform/Page";
import * as Utils_String from "VSS/Core/Util/String";
import { LegacyComponent } from "VSS/Legacy/Legacy";
import { IVssIdentityService } from "VSS/Platform/Identity";
import { ILegacyPlatformService } from "VSS/Legacy/Legacy";
import { IVssPageContext } from "VSS/Platform/Context";
import { IVssComponentContext, VssComponent, WrappedComponent } from "VSS/Platform/Layout";
import { routeUrl } from "VSS/Platform/Location";
import { Tooltip } from "VSSUI/TooltipEx";
import { Location } from "VSSUI/Utilities/Position";
import { VssDetailsList } from "VSSUI/VssDetailsList";
import { VssIcon, VssIconType } from "VSSUI/VssIcon";
import { VssPersona } from 'VSSUI/VssPersona';
import { isFeatureEnabled, isFeatureFlagEnabled} from "VSS/Platform/Feature";


export interface ITestPlanListBaseComponentProps {
    store: TestPlanDirectoryStore;
    actionsCreator: TestPlanDirectoryActionsCreator;
}


export abstract class TestPlanComponentBase<TState extends ITestPlanDirectoryListComponentState>
    extends VssComponent<ITestPlanListBaseComponentProps, TState, IVssComponentContext> {

    constructor(props: ITestPlanListBaseComponentProps, context: IVssComponentContext) {
        super(props);
        this._pageContext = context.pageContext;
    }

    protected static CSS_TESTPLAN_DIRECTORY_LIST = "testplan-directory-list";
    protected static CSS_TESTPLAN_DIRECTORY_ROWITEM = "testplan-directory-rowitem";
    protected static CSS_TESTPLAN_DIRECTORY_GROUP_SPACER = "testplan-group-spacer";
    private static CSS_TESTPLAN_DIRECTORY_COLUMN = "testplan-directory-column";
    private static CSS_TESTPLAN_DIRECTORY_COLUMN_HEADER = "testplan-directory-column-header";

    public componentDidMount() {
            let isSortPlans : boolean = isFeatureFlagEnabled(
                this._pageContext,
                ManageTestPlansCommonConstants.SortPlansAndSuitesAlphabeticallyFF,
                false
            );
        TestPlanDirectoryStore.setSortPlansAndSuitesAlphabeticallyFF(isSortPlans);
    }

    public componentDidUpdate() {

    }

    public render(): JSX.Element {
        
        if (this.state.isLoading) {
            return <LoadingComponent />;
        }
        let items = this.state.items as ITestPlanRow[] | IDirectoryRow[];
        let filteredItems = this._getFilteredItems(this.state.items);

        // Render the view
        return <div className={this._getContainerClassName()}>
            <VssDetailsList
                ariaLabelForGrid={this._getAriaLabelForDetailsList()}
                setKey={this._getDetailsListClassName()} // setKey is required to keep focus on the selected item when the row is re-rendered
                layoutMode={DetailsListLayoutMode.justified}
                constrainMode={ConstrainMode.unconstrained}
                isHeaderVisible={true}
                columns={this.getColumns()}
                className={css(TestPlanComponentBase.CSS_TESTPLAN_DIRECTORY_LIST, this._getDetailsListClassName())}
                items={this.state.items}
                onRenderItemColumn={this._onRenderDirectoryItemColumn}
                selectionMode={SelectionMode.single}
                initialFocusedIndex={document.activeElement === document.body ? 0 : -1}
                getKey={(item: ITestPlanRow) => item.title}
                allocateSpaceForActionsButtonWhileHidden={true}
                actionsColumnKey={TestPlanPivotColumnKeys.Title}
                shouldDisplayActions={this._shouldDisplayActions}
                getMenuItems={this._getMenuItems}
                onRenderRow={(props: IDetailsRowProps) => this._getRowElement(props)}
            />
            <div className="visually-hidden" aria-live="polite">{Utils_String.format(Resources.TestResultsMessage, filteredItems.length)}</div>
        </div>;
    }

    private _getFilteredItems(items: any[]): any[] {
        let filteredItems: any[] = [];
        if (items && items.length > 0) {
            items.forEach(item => {
                if (!item.isGroupRow) {
                    filteredItems.push(item);
                }
            });
        }
        return filteredItems;
    }

    protected abstract _onRenderDirectoryItemColumn: () => JSX.Element;

    protected abstract _getContainerClassName: () => string;

    protected abstract _getDetailsListClassName: () => string;

    protected abstract _getAriaLabelForDetailsList: () => string;

    /**
    * Render the row. Renders the Details row with a key down action so we can control keyboard events. 
    * @param props to render for the row
    */
    protected _getRowElement(props: IDetailsRowProps) {
        return (
            <div >
                <DetailsRow
                    {...props}
                />
            </div>
        );
    }

    /**
     * Get columns for testplan directory page
    */
    protected getColumns(): IColumn[] {
        const columnActionsMode = this._getColumnActionsMode();

        return [
            {
                fieldName: TestPlanPivotColumnKeys.Title,
                key: TestPlanPivotColumnKeys.Title,
                name: Resources.TitleText,
                minWidth: 400,
                maxWidth: 600,
                headerClassName: TestPlanComponentBase.CSS_TESTPLAN_DIRECTORY_COLUMN_HEADER,
                className: TestPlanComponentBase.CSS_TESTPLAN_DIRECTORY_COLUMN,
                ariaLabel: Resources.TitleText,
                isResizable: true,
                columnActionsMode: columnActionsMode,
                onColumnClick: this._onColumnClick,
                isSorted: Utils_String.equals(this.state.sortedColumn!, TestPlanPivotColumnKeys.Title, true),
                isSortedDescending: this.state.isSortedDescending!
            },
            {
                fieldName: TestPlanPivotColumnKeys.State,
                key: TestPlanPivotColumnKeys.State,
                name: Resources.StateText,
                isResizable: true,
                minWidth: 90,
                maxWidth: 300,
                headerClassName: TestPlanComponentBase.CSS_TESTPLAN_DIRECTORY_COLUMN_HEADER,
                className: TestPlanComponentBase.CSS_TESTPLAN_DIRECTORY_COLUMN,
                ariaLabel: Resources.StateText,
                columnActionsMode: columnActionsMode,
                onColumnClick: this._onColumnClick,
                isSorted: Utils_String.equals(this.state.sortedColumn!, TestPlanPivotColumnKeys.State, true),
                isSortedDescending: this.state.isSortedDescending!
            },
            {
                fieldName: TestPlanPivotColumnKeys.Area,
                key: TestPlanPivotColumnKeys.Area,
                name: Resources.AreaPath,
                isResizable: true,
                minWidth: 190,
                maxWidth: 400,
                headerClassName: TestPlanComponentBase.CSS_TESTPLAN_DIRECTORY_COLUMN_HEADER,
                className: TestPlanComponentBase.CSS_TESTPLAN_DIRECTORY_COLUMN,
                ariaLabel: Resources.AreaPath,
                columnActionsMode: columnActionsMode,
                onColumnClick: this._onColumnClick,
                isSorted: Utils_String.equals(this.state.sortedColumn!, TestPlanPivotColumnKeys.Area, true),
                isSortedDescending: this.state.isSortedDescending!
            },
            {
                fieldName: TestPlanPivotColumnKeys.Iteration,
                key: TestPlanPivotColumnKeys.Iteration,
                name: Resources.IterationPathText,
                isResizable: true,
                minWidth: 190,
                maxWidth: 400,
                headerClassName: TestPlanComponentBase.CSS_TESTPLAN_DIRECTORY_COLUMN_HEADER,
                className: TestPlanComponentBase.CSS_TESTPLAN_DIRECTORY_COLUMN,
                ariaLabel: Resources.IterationPathText,
                columnActionsMode: columnActionsMode,
                onColumnClick: this._onColumnClick,
                isSorted: Utils_String.equals(this.state.sortedColumn!, TestPlanPivotColumnKeys.Iteration, true),
                isSortedDescending: this.state.isSortedDescending!
            },
            {
                fieldName: TestPlanPivotColumnKeys.AssignedTo,
                key: TestPlanPivotColumnKeys.AssignedTo,
                name: Resources.AssignedTo,
                isResizable: true,
                minWidth: 190,
                maxWidth: 400,
                headerClassName: TestPlanComponentBase.CSS_TESTPLAN_DIRECTORY_COLUMN_HEADER,
                className: TestPlanComponentBase.CSS_TESTPLAN_DIRECTORY_COLUMN,
                ariaLabel: Resources.AssignedTo,
                columnActionsMode: columnActionsMode,
                onColumnClick: this._onColumnClick,
                isSorted: Utils_String.equals(this.state.sortedColumn!, TestPlanPivotColumnKeys.AssignedTo, true),
                isSortedDescending: this.state.isSortedDescending!
            },
            {
                fieldName: TestPlanPivotColumnKeys.testPlanId,
                key: TestPlanPivotColumnKeys.testPlanId,
                name: Resources.IdText,
                minWidth: 70,
                maxWidth: 100,
                headerClassName: TestPlanComponentBase.CSS_TESTPLAN_DIRECTORY_COLUMN_HEADER,
                className: TestPlanComponentBase.CSS_TESTPLAN_DIRECTORY_COLUMN,
                ariaLabel: Resources.IdText,
                isResizable: true,
                columnActionsMode: columnActionsMode,
                onColumnClick: this._onColumnClick,
                isSorted: Utils_String.equals(this.state.sortedColumn!, TestPlanPivotColumnKeys.testPlanId, true),
                isSortedDescending: this.state.isSortedDescending!
            }
        ];
    }

    /**
     * Column click handler for sorting
     * @param event
     * @param column
     */
    protected _onColumnClick = (event?: React.MouseEvent<HTMLElement>, column?: IColumn): void => {
        if (!!column) {
            this.props.actionsCreator.changeColumnSort(column.fieldName);
        }
    }

    /**
     * Render directory cell depending on the column
     * @param item
     * @param index
     * @param column
     */
    protected _onRenderItemColumn(item?: ITestPlanRow, index?: number, column?: IColumn, isGrouped?: boolean): JSX.Element | null {
        if (!item || !column) {
            return null;
        }

        switch (column.fieldName) {
            case TestPlanPivotColumnKeys.testPlanId:
                return this._getIdColumnElement(item, index!, column);
            case TestPlanPivotColumnKeys.Title:
                return this._getTitleColumnElement(item, index!, column, isGrouped!);

            case TestPlanPivotColumnKeys.State:
                return this._getStateColumnElement(item, index!, column);

            case TestPlanPivotColumnKeys.Area:
                return this._getAreaColumnElement(item, index!, column);

            case TestPlanPivotColumnKeys.Iteration:
                return this._getIterationColumnElement(item, index!, column);

            case TestPlanPivotColumnKeys.AssignedTo:
                return this._getAssignedToColumnElement(item, index!, column);

            default:
                return null;
        }
    }

    /**
    * Default is clickable. Override to change default
    */
    protected _getColumnActionsMode(): ColumnActionsMode {
        return ColumnActionsMode.disabled;
    }

    private _shouldDisplayActions = (item: IDirectoryRow): boolean => {
        // No context menu for group rows or empty rows
        return !item.isGroupRow
            && item.directoryRow.teamId !== TestPlanDirectoryStore.MY_FAVORITES_EMPTY_CONTENT_ID
            && isAdvancedTestExtensionEnabled(this._pageContext);
    }

    private _getMenuItems = (directoryRow: IDirectoryRow): IContextualMenuItem[] => {
        if (directoryRow.isGroupRow) {
            return null as any;
        }

        const testPlanRow = directoryRow.directoryRow as ITestPlanRow;
        const menuItems: IContextualMenuItem[] = [
            this._createContextMenuItem(testPlanRow, Resources.Edit, this._onEditClick, "ms-Icon--Edit"),
            this._createContextMenuItem(testPlanRow, Resources.Delete, this._onDeleteClick, "ms-Icon--Delete")
        ];

        return menuItems;
    }


    private _createContextMenuItem(testPlanRow: ITestPlanRow, name: string, onClickCallback: ((testPlanRow: ITestPlanRow) => void), iconClass: string): IContextualMenuItem {
        return {
            name: name,
            onClick: () => {
                onClickCallback(testPlanRow);
            },
            key: name,
            iconProps: {
                className: "fabric-icon " + iconClass
            }
        };
    }

    private _onEditClick = (row: ITestPlanRow): void => {
        const planId = Number(row.testPlanId);
        const legacyService = this.context.pageContext.getService<ILegacyPlatformService>("ILegacyPlatformService");

        legacyService.requireModules(["WorkItemTracking/Scripts/TFS.WorkItemTracking.Controls"]).then(([workItemControlsModule]) => {
            workItemControlsModule.WorkItemFormDialog.showWorkItemById(planId, this._pageContext, {
                save: (workItem: any): void => {
                    this._onSave(row, workItem);
                }
            });
        });
    }

    private _onSave = (row: ITestPlanRow, patchedPlan: any): void => {
        const payload: ITestPlanRow = Object.assign({}, row, { fields: row.fields });
        payload.title = patchedPlan.getFieldValue(WorkItemField.title);
        payload.fields.areaPath = patchedPlan.getFieldValue(WorkItemField.areaPath);
        payload.fields.state = patchedPlan.getFieldValue(WorkItemField.workItemState);
        payload.fields.assignedTo = patchedPlan.getIdentityFieldValue(WorkItemField.assignedTo);
        payload.fields.iterationPath = patchedPlan.getFieldValue(WorkItemField.iterationPath);

        TestPlanDirectoryActionsCreator
            .getInstance()
            .patchPlan(payload);
    }

    private _onDeleteClick = (row: ITestPlanRow): void => {
        const legacyService = this.context.pageContext.getService<ILegacyPlatformService>("ILegacyPlatformService");

        legacyService.requireModules(["WorkItemTracking/Scripts/TestWorkItemDelete/TFS.TestWorkItemDelete.Dialog"]).then(([testWorkItemDeleteModule]) => {
            const deleteDialog = new testWorkItemDeleteModule.TestPlanDeleteConfirmationDialog({ projectId: this._getProjectIdFromContext() });
            const planId = Number(row.testPlanId);
            deleteDialog.showDialog(planId, () => { this._onConfirmDelete(planId, deleteDialog); });
        });
    }



    /**
* Get project id from context
*/
    private _getProjectIdFromContext(): string {
        const tfsPageData = this._pageContext.getService<ITfsPageService>("ITfsPageService").getData();
        if (tfsPageData && tfsPageData.project) {
            const project = tfsPageData.project;
            return project.id;
        }
        else {
            return "";
        }
    }

    private _onConfirmDelete = (testPlanId: number, deleteDialog: any): void => {
        const successHandler: { (): void } = () => { this._handleSuccess(testPlanId); };
        deleteDialog.deleteTestWorkItem(TelemetryConstants.testHubPage, TelemetryConstants.areaTestManagement, testPlanId, false, successHandler);
    }

    private _handleSuccess = (testPlanId: number): void => {
        TestPlanDirectoryActionsCreator
            .getInstance()
            .deletePlan(testPlanId);
    }

    /**
     * Render title column
     * @param item
     * @param index
     * @param column
     */
    private _getTitleColumnElement = (item: ITestPlanRow, index: number, column: IColumn, isGrouped: boolean): JSX.Element => {
        // An item is considered as favorite iff it is favorited or the favorite call is in-progress
        const isFavorite = item.favoriteState === FavoriteState.Favorited || item.favoriteState === FavoriteState.Favoriting;

        // If this column is part of a group, it needs to be indented 
        let groupSpacer = isGrouped ? <div className={TestPlanComponentBase.CSS_TESTPLAN_DIRECTORY_GROUP_SPACER} /> : null;

        const titleElement = item.isDeleted ? item.title :
            <Link className="testplan-title" href={this._getPlanUrl(item)} removeUnderline={true}>
                <span>{item.title}</span>
            </Link>;

        // Title column
        return (<div className={TestPlanComponentBase.CSS_TESTPLAN_DIRECTORY_ROWITEM} key={`${item.teamId}.Name`}>
            {groupSpacer}
            <div className="testplan-row-title">
                <Tooltip overflowOnly={true} text={item.title}>
                    <div className="flex-tooltip-host">
                        <VssIcon className="testplan-icon" iconName="test-plan" iconType={VssIconType.bowtie} />
                        {titleElement}
                    </div>
                </Tooltip>
            </div>
            <div className={`testplan-row-favorite ${isFavorite ? "favorited" : ""}`}>
                <div
                    className="visually-hidden"
                    aria-live="assertive"
                >
                    {isFavorite ? "On" : "Off"}
                </div>
                <WrappedComponent
                    wrappedType={"favoritesStarButton"}
                    dependencies={["ms.vss-favorites.favorites-star"]}
                    wrappedProps={{
                        isFavorited: isFavorite,
                        onToggleFavorite: () => {
                            if (isFavorite) {
                                this.props.actionsCreator.unfavoriteTeamTestPlan(item);
                            }
                            else {
                                this.props.actionsCreator.favoriteTeamTestPlan(item);
                            }
                        }
                    }} />
            </div>
        </div>);

    }

    /**
     * Render state column
     * @param item
     * @param index
     * @param column
     */
    private _getStateColumnElement = (item: ITestPlanRow, index: number, column: IColumn): JSX.Element => {
        const componentClassName = "testplan-state-cell-component";
        const tooltipId = getTooltipId(componentClassName);

        return <div className={componentClassName}>
            <Tooltip
                anchorOrigin={{ horizontal: Location.center, vertical: Location.end }}
                id={tooltipId}
                text={item.fields.state}
                tooltipOrigin={{ horizontal: Location.center, vertical: Location.start }}
            >
                <span>{item.fields.state}</span>
            </Tooltip>
        </div>;
    }

    private _getIdColumnElement = (item: ITestPlanRow, index: number, column: IColumn): JSX.Element => {
        const componentClassName = "testplan-id-cell-component";
        const tooltipId = getTooltipId(componentClassName);

        return <div className={componentClassName}>
            <Tooltip
                anchorOrigin={{ horizontal: Location.center, vertical: Location.end }}
                id={tooltipId}
                text={item.fields.testPlanId.toString()}
                tooltipOrigin={{ horizontal: Location.center, vertical: Location.start }}
            >
                <span>{item.fields.testPlanId}</span>
            </Tooltip>
        </div>;
    }
    /**
     * Render area column
     * @param item
     * @param index
     * @param column
     */
    private _getAreaColumnElement = (item: ITestPlanRow, index: number, column: IColumn): JSX.Element => {
        const componentClassName = "testplan-area-cell-component";
        const tooltipId = getTooltipId(componentClassName);

        return <div className={componentClassName}>
            <Tooltip
                anchorOrigin={{ horizontal: Location.center, vertical: Location.end }}
                id={tooltipId}
                text={item.fields.areaPath}
                tooltipOrigin={{ horizontal: Location.center, vertical: Location.start }}
            >
                <span>{item.fields.areaPath}</span>
            </Tooltip>
        </div>;
    }

    /**
     * Render iteration column
     * @param item
     * @param index
     * @param column
     */
    private _getIterationColumnElement = (item: ITestPlanRow, index: number, column: IColumn): JSX.Element => {
        const componentClassName = "testplan-iteration-cell-component";
        const tooltipId = getTooltipId(componentClassName);

        return <div className={componentClassName}>
            <Tooltip
                anchorOrigin={{ horizontal: Location.center, vertical: Location.end }}
                id={tooltipId}
                text={item.fields.iterationPath}
                tooltipOrigin={{ horizontal: Location.center, vertical: Location.start }}
            >
                <span>{item.fields.iterationPath}</span>
            </Tooltip>
        </div>;
    }

    // /**
    //  * Render assignedTo column
    //  * @param item
    //  * @param index
    //  * @param column
    //  */
    private _getAssignedToColumnElement = (item: ITestPlanRow, index: number, column: IColumn): JSX.Element => {

        const componentClassName = "testplan-assignedto-cell-component";
        const tooltipId = getTooltipId(componentClassName);
        const isUnassigned = !item.fields.assignedTo || !item.fields.assignedTo.id;
        const identityService = this._pageContext.getService<IVssIdentityService>("IVssIdentityService");
        const imageSource = isUnassigned ? "" : item.fields.assignedTo.imageUrl;

        return (<div className={componentClassName}>
                    <div className={"identity-container"}>
                        <div className={"persona-container"}>
                            <VssPersona displayName={item.fields.assignedTo.displayName} imageUrl={imageSource} size="small" />
                        </div>
                        <span className={"name-container"} aria-label={item.fields.assignedTo.displayName}>
                            {item.fields.assignedTo.displayName}
                        </span>
                    </div>
                </div>);
    }


    private _getPlanUrl = (item: ITestPlanRow) => {

        const testHubRoute: string = "ms.vss-test-web.test-basic-route";
        const tfsPageData = this._pageContext.getService<ITfsPageService>("ITfsPageService").getData();

        const routeValues: { [key: string]: string } = {};
        routeValues["project"] = tfsPageData && tfsPageData.project ? tfsPageData.project.name : "";
        routeValues["planId"] = item.testPlanId.toString();
       
        // if new test plans hub feature is enabled navigate to new hub
        if (isFeatureEnabled(this._pageContext, "ms.vss-test-web.testplans-hub-refresh-feature", false)) {
            const newTestHubRoute: string = "ms.vss-test-web.testplans-hub-refresh-route";
            return routeUrl(this.context.pageContext, newTestHubRoute, routeValues);
        }   
        return routeUrl(this.context.pageContext, testHubRoute, routeValues);
    }

    protected _pageContext: IVssPageContext;
}
